import React, { useMemo } from "react";
import { Box } from "@mui/material";
import parse, {
	DOMNode,
	Element,
	HTMLReactParserOptions,
	domToReact,
} from "html-react-parser";
import {
	B2B_NOTES_ALLOWED_HTML_TAGS,
	resolveB2bNotesHtml,
} from "../../../utils/b2bNotesRichTextUtils";
import "./b2bNotesRich.scss";

const SAFE_HTTP_LINK_PATTERN = /^https?:\/\//i;
const ALLOWED_TAGS = new Set<string>(B2B_NOTES_ALLOWED_HTML_TAGS);
const DROP_WITH_CONTENT_TAGS = new Set(["script", "style", "iframe", "img"]);

export interface RichHtmlNotesProps {
	html?: string | null;
	className?: string;
	emptyText?: string;
}

function transformNode(domNode: DOMNode): React.ReactElement | undefined {
	// Use a structural guard rather than instanceof. The parser and application
	// can resolve separate domhandler module instances in bundled/test builds.
	if (!("name" in domNode) || !("children" in domNode)) {
		return undefined;
	}

	const element = domNode as Element;
	const tagName = element.name.toLowerCase();

	if (DROP_WITH_CONTENT_TAGS.has(tagName)) {
		return <></>;
	}

	const children = domToReact(element.children as DOMNode[], PARSER_OPTIONS);

	if (!ALLOWED_TAGS.has(tagName)) {
		return <>{children}</>;
	}

	if (tagName === "a") {
		const href = element.attribs?.href?.trim();
		if (!href || !SAFE_HTTP_LINK_PATTERN.test(href)) {
			return <>{children}</>;
		}

		return (
			<a href={href} target="_blank" rel="noopener noreferrer">
				{children}
			</a>
		);
	}

	// Recreate every allowed element without source attributes. Only a validated
	// href is retained above, matching the Baqaal allowlist contract.
	return React.createElement(tagName, null, children);
}

const PARSER_OPTIONS: HTMLReactParserOptions = { replace: transformNode };

const RichHtmlNotes: React.FC<RichHtmlNotesProps> = ({
	html,
	className,
	emptyText = "N/A",
}) => {
	const resolvedHtml = useMemo(() => resolveB2bNotesHtml(html), [html]);

	const parsedContent = useMemo(() => {
		if (!resolvedHtml) return null;
		return parse(resolvedHtml, PARSER_OPTIONS);
	}, [resolvedHtml]);

	if (!resolvedHtml) {
		return <Box className={`b2b-notes-rich-empty ${className ?? ""}`.trim()}>{emptyText}</Box>;
	}

	return (
		<Box className={`b2b-notes-rich rich ${className ?? ""}`.trim()} component="div">
			{parsedContent}
		</Box>
	);
};

export default RichHtmlNotes;
